import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { useTranslation } from 'react-i18next';

import type { RecaneCaneGroup, RecanePrintLabelType } from './types';

type PrintSelectionStepProps = {
  groups: RecaneCaneGroup[];
  selectedCanes: string[];
  onToggleCane: (cane: string) => void;
  onToggleAllCanes: (checked: boolean) => void;
  labelTypes: RecanePrintLabelType[];
  onToggleLabelType: (labelType: RecanePrintLabelType) => void;
};

export const PrintSelectionStep = ({
  groups,
  selectedCanes,
  onToggleCane,
  onToggleAllCanes,
  labelTypes,
  onToggleLabelType,
}: PrintSelectionStepProps) => {
  const { t } = useTranslation();

  const labelTypeOptions: { value: RecanePrintLabelType; label: string }[] = [
    { value: 'CaneTabLabel', label: t('embryo.caneTabLabel') },
    { value: 'GobletLabel', label: t('embryo.gobletLabel') },
    { value: 'StrawLabel', label: t('embryo.strawLabel') },
  ];

  const allSelected =
    groups.length > 0 && selectedCanes.length === groups.length;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h3 className="text-lg font-semibold">
          {t('embryo.selectLabelTypes')}
        </h3>
        <div className="flex flex-wrap gap-4">
          {labelTypeOptions.map((option) => (
            <div key={option.value} className="flex items-center gap-2">
              <Checkbox
                inputId={`label-type-${option.value}`}
                checked={labelTypes.includes(option.value)}
                onChange={() => onToggleLabelType(option.value)}
              />
              <label htmlFor={`label-type-${option.value}`}>
                {option.label}
              </label>
            </div>
          ))}
        </div>
        {labelTypes.length === 0 && (
          <Message severity="info" text={t('embryo.noLabelTypesSelected')} />
        )}
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-lg font-semibold">
          {t('embryo.selectCanesToPrint')}
        </h3>
        <div className="flex items-center gap-2">
          <Checkbox
            inputId="select-all-canes"
            checked={allSelected}
            disabled={groups.length === 0}
            onChange={() => onToggleAllCanes(!allSelected)}
          />
          <label htmlFor="select-all-canes">{t('embryo.selectAllCanes')}</label>
        </div>
        <div className="flex flex-col gap-2">
          {groups.map((group) => (
            <div
              key={group.cane}
              className="flex items-start gap-2 rounded border border-gray-200 p-2"
            >
              <Checkbox
                inputId={`cane-${group.cane}`}
                checked={selectedCanes.includes(group.cane)}
                onChange={() => onToggleCane(group.cane)}
              />
              <div className="flex flex-col gap-1">
                <label htmlFor={`cane-${group.cane}`} className="font-medium">
                  {group.cane}
                </label>
                <span className="text-color-secondary text-sm">
                  {group.embryoNames.join(', ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
